import { BILLWERK_SDK_ORIGINS } from '../sdkOrigins';

interface ScriptState {
  promise: Promise<void>;
  element: HTMLScriptElement;
}

const ALLOWED_ORIGINS = new Set(BILLWERK_SDK_ORIGINS);

function assertAllowedOrigin(url: string): void {
  let origin: string;
  try {
    ({ origin } = new URL(url));
  } catch {
    throw new Error(`Invalid Billwerk SDK URL: "${url}"`);
  }
  if (!ALLOWED_ORIGINS.has(origin)) {
    throw new Error(`Billwerk SDK URL origin "${origin}" is not allowed. Permitted origins: ${[...ALLOWED_ORIGINS].join(', ')}`);
  }
}

const loadedScripts = new Map<string, ScriptState>();
let configuredSdkUrl: string | null = null;
const preloadedUrls = new Set<string>();

export function configureBillwerkSdkUrl(url: string): void {
  assertAllowedOrigin(url);
  configuredSdkUrl = url;
}

export function preloadBillwerkScript(url: string): void {
  if (typeof document === 'undefined') {
    return; // SSR - skip silently
  }

  assertAllowedOrigin(url);

  if (preloadedUrls.has(url) || loadedScripts.has(url)) {
    return;
  }

  const link = document.createElement('link');
  link.rel = 'preload';
  link.href = url;
  link.as = 'script';
  document.head.appendChild(link);

  preloadedUrls.add(url);
}

const DEFAULT_TIMEOUT_MS = 15_000;

export function loadBillwerkScript({ timeoutMs = DEFAULT_TIMEOUT_MS }: { timeoutMs?: number } = {}): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('loadBillwerkScript can only be called in browser environment'));
  }

  if (!configuredSdkUrl) {
    return Promise.reject(new Error('Billwerk SDK URL not configured. Call configureBillwerkSdkUrl() first.'));
  }

  const url = configuredSdkUrl;

  const existing = loadedScripts.get(url);
  if (existing) {
    return existing.promise;
  }

  if (typeof window !== 'undefined' && (window.BillwerkJS || window.SubscriptionJS)) {
    const resolvedPromise = Promise.resolve();
    const dummyScript = document.createElement('script');
    loadedScripts.set(url, { promise: resolvedPromise, element: dummyScript });
    return resolvedPromise;
  }

  const script = document.createElement('script');
  script.src = url;
  script.async = true;

  const loadPromise = new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      loadedScripts.delete(url); // Allow retry on failure
      script.onload = null;
      script.onerror = null;
      script.parentNode?.removeChild(script);
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`Billwerk SDK script load timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    script.onload = () => {
      clearTimeout(timer);
      if (window.BillwerkJS || window.SubscriptionJS) {
        resolve();
      } else {
        cleanup();
        reject(new Error('Billwerk SDK loaded but not available on window'));
      }
    };

    script.onerror = () => {
      clearTimeout(timer);
      cleanup();
      reject(new Error('Failed to load Billwerk SDK script'));
    };

    document.head.appendChild(script);
  });

  loadedScripts.set(url, { promise: loadPromise, element: script });
  return loadPromise;
}

export function getBillwerkSDK(): typeof BillwerkJS {
  const sdk = window.BillwerkJS || window.SubscriptionJS;
  if (!sdk) {
    throw new Error('Billwerk SDK not loaded. Call loadBillwerkScript() first.');
  }
  return sdk;
}

export function unloadBillwerkScript(): void {
  if (!configuredSdkUrl) {
    return;
  }

  const state = loadedScripts.get(configuredSdkUrl);
  if (state?.element?.parentNode) {
    state.element.parentNode.removeChild(state.element);
  }
  loadedScripts.delete(configuredSdkUrl);
}

/** @internal */
export function resetScriptLoader(): void {
  for (const state of loadedScripts.values()) {
    if (state.element?.parentNode) {
      state.element.parentNode.removeChild(state.element);
    }
  }
  loadedScripts.clear();
  preloadedUrls.clear();
  configuredSdkUrl = null;
}
